/**
 * Génération de residenceCode unique garantie.
 * Vérifie dans Firestore qu'aucune autre résidence n'a déjà le même code
 * avant de le retourner. En cas de collision, génère un suffixe aléatoire
 * jusqu'à trouver un code libre.
 */

import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { generateResidenceCode } from '@/types/residence';

const isCodeTaken = async (code: string): Promise<boolean> => {
  const snap = await getDocs(
    query(collection(db, 'residences'), where('residenceCode', '==', code), limit(1))
  );
  return !snap.empty;
};

const randomSuffix = (): string =>
  Math.random().toString(36).slice(2, 5).toUpperCase().replace(/[^A-Z0-9]/g, '0');

export const generateUniqueResidenceCode = async (
  postalCode: string,
  firestoreId: string
): Promise<string> => {
  const candidate = generateResidenceCode(postalCode, firestoreId);

  if (!(await isCodeTaken(candidate))) return candidate;

  // Collision détectée → essayer des suffixes aléatoires
  const cleanPostal = (postalCode || '00000').replace(/\D/g, '').slice(0, 5).padStart(5, '0');
  for (let i = 0; i < 10; i++) {
    const alt = `RES-${cleanPostal}-${randomSuffix()}`;
    if (!(await isCodeTaken(alt))) return alt;
  }

  // Fallback ultra-safe : timestamp en base36
  return `RES-${cleanPostal}-${Date.now().toString(36).slice(-3).toUpperCase()}`;
};
